import { ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import { exhaustMap, mergeMap, switchMap } from 'rxjs/operators';

import {
    updateCardAccountApplication as updateCardAccountApplicationAction,
    updateCardAccountApplicationForOnboarding as updateCardAccountApplicationForOnboardingAction,
    updateCardAccountApplicationForOnboardingSuccess,
    updateCardAccountApplicationForOnboardingError,
    updateCardAccountApplicationSuccess,
    updateCardAccountApplicationError,
    fetchCardAccountApplicationForOnboarding as fetchCardAccountApplicationForOnboardingAction,
    fetchCardAccountApplicationForOnboardingSuccess,
    fetchCardAccountApplicationForOnboardingError,
    fetchCardAccountApplication,
    fetchCardAccountApplicationSuccess,
    fetchCardAccountApplicationError,
    creditUnfreezeCardAccountApplication as creditUnfreezeCardAccountApplicationAction,
    creditUnfreezeCardAccountApplicationSuccess,
    creditUnfreezeCardAccountApplicationError,
    cardAccountApplicationStartPolling,
} from '@perpay-web/fintech/actions/entities/cardAccountApplications';
import { fetchFeatures } from '@perpay-web/fintech/actions/entities/features';
import {
    payrollPaymentSetupComplete,
    payrollPaymentSetupStart,
} from '@perpay-web/fintech/actions/payrollPaymentSetup';
import { routeToLocation } from '@perpay-web/fintech/actions/router';
import { cardUpdateUserInfo } from '@perpay-web/fintech/actions/ui/cardEditAddress';
import {
    fetchFeaturesForOnboarding as fetchFeaturesForOnboardingAction,
    fetchFeaturesForOnboardingError,
    fetchFeaturesForOnboardingSuccess,
} from '@perpay-web/fintech/actions/ui/cardOnboarding';
import {
    getCardApplicationStepFromStatus,
    PAYROLL_PAYMENT_SETUP_STEP,
} from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { SUCCESS_STEP } from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { cardApplicationSetSteps } from '@perpay-web/fintech/components/screens/CardApplication/useCardApplicationContext';
import {
    BACKEND_CREATE_DEDUCTION,
    CARD_PROCESS_APPLICATION,
    CARD_PROCESS_APPLICATION_ADDRESS,
    STORE_FETCH_FEATURES_ERROR,
    STORE_REPLACE_DEDUCTION,
    STORE_REPLACE_FEATURES,
} from '@perpay-web/fintech/constants/actionTypes';
import {
    NEW,
    CONTINUE_POLLING_STATUSES,
} from '@perpay-web/fintech/constants/cardAccountApplicationStatuses';
import { CARD_APPLY } from '@perpay-web/fintech/constants/featureDetails';
import statesIneligibleForCard from '@perpay-web/fintech/constants/statesIneligibleForCardCardSignupMVP';
import {
    TAKEOVER_STEP as TAKEOVER_MVP_2_STEP,
    REVIEW_COMPANY_STEP,
    APPLICATION_ERROR_STEP as APPLICATION_ERROR_MVP_2_STEP,
    PINWHEEL_STEP,
    CARD_ONBOARDING_MVP_2_STEPS,
} from '@perpay-web/fintech/constants/steps/cardOnboardingMVP2Steps';
import * as metalSteps from '@perpay-web/fintech/constants/steps/cardOnboardingMetalSteps';
import { createCardAccountApplicationsDataModule } from '@perpay-web/fintech/dataModules/createCardAccountApplications';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { getCardFeatureDetails } from '@perpay-web/fintech/selectors/entities/features';
import { getMVP2StepFromStatus } from '@perpay-web/fintech/utils/cardAccountApplicationMVP2Utils';
import { getMetalStepFromStatus } from '@perpay-web/fintech/utils/cardAccountApplicationMetalUtils';
import { reduxStepsSetStep } from '@perpay-web/hooks/useReduxSteps';
import { dispatchObservable } from '@perpay-web/observable/dispatchObservable';
import { getRouterState } from '@perpay-web/services/router';

export const fetchFeaturesForOnboarding = (action$, state$) =>
    action$.pipe(
        ofType(fetchFeaturesForOnboardingAction().type),
        exhaustMap(() =>
            dispatchObservable({
                action$,
                state$,
                initialDispatch: () => [fetchFeatures()],
                waitFor: [STORE_REPLACE_FEATURES],
                waitForDispatch: (state) => {
                    const cardFeatureDetails = getCardFeatureDetails(state);

                    const maybeRedirectAction = !cardFeatureDetails.includes(
                        CARD_APPLY,
                    )
                        ? [
                              routeToLocation({
                                  path: paths.dashboard.path,
                                  replace: true,
                              }),
                          ]
                        : [];

                    return [
                        ...maybeRedirectAction,
                        fetchFeaturesForOnboardingSuccess(),
                    ];
                },
                errors: [STORE_FETCH_FEATURES_ERROR],
                errorDispatch: () => [
                    routeToLocation({
                        path: paths.dashboard.path,
                        replace: true,
                    }),
                    fetchFeaturesForOnboardingError(),
                ],
            }),
        ),
    );

export const fetchCardAccountApplicationForOnboarding = (action$, state$) =>
    action$.pipe(
        ofType(fetchCardAccountApplicationForOnboardingAction().type),
        exhaustMap(() =>
            dispatchObservable({
                action$,
                state$,
                initialDispatch: () => [fetchCardAccountApplication()],
                waitFor: [fetchCardAccountApplicationSuccess().type],
                waitForDispatch: (state, results) => {
                    const { previousLocation } =
                        getRouterState().location.state || {};

                    const shouldSkipFirstStep =
                        previousLocation === paths.cardLearn.path;

                    const cardAccountApplication = results[0].payload;
                    if (!cardAccountApplication.status) {
                        const returnActions = [
                            fetchCardAccountApplicationForOnboardingSuccess({}),
                        ];
                        if (shouldSkipFirstStep) {
                            returnActions.push(
                                reduxStepsSetStep(
                                    CARD_ONBOARDING_MVP_2_STEPS,
                                    REVIEW_COMPANY_STEP,
                                ),
                                reduxStepsSetStep(
                                    Object.values(metalSteps),
                                    metalSteps.REVIEW_COMPANY_STEP,
                                ),
                                routeToLocation({
                                    path: paths.cardOnboarding.path,
                                    props: { previousLocation },
                                }),
                            );
                        } else {
                            returnActions.push(
                                reduxStepsSetStep(
                                    CARD_ONBOARDING_MVP_2_STEPS,
                                    TAKEOVER_MVP_2_STEP,
                                ),
                                reduxStepsSetStep(
                                    Object.values(metalSteps),
                                    metalSteps.TAKEOVER_STEP,
                                ),
                                routeToLocation({
                                    path: paths.cardOnboarding.path,
                                    props: { previousLocation },
                                }),
                            );
                        }
                        return returnActions;
                    }
                    const returnActions = [
                        reduxStepsSetStep(
                            CARD_ONBOARDING_MVP_2_STEPS,
                            getMVP2StepFromStatus(
                                cardAccountApplication.status,
                            ),
                        ),
                        reduxStepsSetStep(
                            Object.values(metalSteps),
                            getMetalStepFromStatus(
                                cardAccountApplication.status,
                            ),
                        ),
                        fetchCardAccountApplicationForOnboardingSuccess(
                            cardAccountApplication,
                        ),
                    ];
                    if (cardAccountApplication.status === NEW) {
                        returnActions.push(
                            cardAccountApplicationStartPolling(
                                cardAccountApplication.uuid,
                            ),
                        );
                    } else if (
                        !CONTINUE_POLLING_STATUSES.includes(
                            cardAccountApplication.status,
                        )
                    ) {
                        const step = getCardApplicationStepFromStatus(
                            cardAccountApplication.status,
                        );
                        returnActions.push(cardApplicationSetSteps(step));
                    }
                    return returnActions;
                },
                errors: [fetchCardAccountApplicationError().type],
                errorDispatch: (result) => {
                    const error = result.payload;
                    return [
                        fetchCardAccountApplicationForOnboardingError(error),
                        reduxStepsSetStep(
                            CARD_ONBOARDING_MVP_2_STEPS,
                            APPLICATION_ERROR_MVP_2_STEP,
                        ),
                        reduxStepsSetStep(
                            Object.values(metalSteps),
                            metalSteps.APPLICATION_ERROR_STEP,
                        ),
                    ];
                },
            }),
        ),
    );

export const creditUnfreezeCardAccountApplication = (action$, state$) =>
    action$.pipe(
        ofType(creditUnfreezeCardAccountApplicationAction().type),
        exhaustMap((action) =>
            dispatchObservable({
                action$,
                state$,
                initialDispatch: () => {
                    const cardAccountApplicationUUID = action.payload;
                    const requestBody = { credit_unfreeze_retry: true };
                    return [
                        updateCardAccountApplicationAction(
                            requestBody,
                            cardAccountApplicationUUID,
                        ),
                    ];
                },
                waitFor: [updateCardAccountApplicationSuccess().type],
                waitForDispatch: (state, results) => {
                    const cardAccountApplication = results[0].payload;
                    const { status } = cardAccountApplication;

                    // If status is still clarity_frozen after retry, indicate error
                    if (status === 'clarity_frozen') {
                        return [
                            creditUnfreezeCardAccountApplicationError({
                                isClarityFrozen: true,
                            }),
                        ];
                    }

                    const returnActions = [
                        cardAccountApplicationStartPolling(
                            cardAccountApplication.uuid,
                        ),
                        creditUnfreezeCardAccountApplicationSuccess(),
                    ];
                    return returnActions;
                },
                errors: [updateCardAccountApplicationError().type],
                errorDispatch: (result) => {
                    const error = result.payload;
                    return [creditUnfreezeCardAccountApplicationError(error)];
                },
            }),
        ),
    );

export const updateCardAccountApplicationForOnboarding = (action$, state$) =>
    action$.pipe(
        ofType(updateCardAccountApplicationForOnboardingAction().type),
        exhaustMap((action) =>
            dispatchObservable({
                action$,
                state$,
                initialDispatch: () => {
                    const { cardAccountApplicationUUID, requestBody } =
                        action.payload;
                    return [
                        updateCardAccountApplicationAction(
                            requestBody,
                            cardAccountApplicationUUID,
                        ),
                    ];
                },
                waitFor: [updateCardAccountApplicationSuccess().type],
                waitForDispatch: (state, results) => {
                    const cardAccountApplication = results[0].payload;
                    if (!cardAccountApplication.status) {
                        return [
                            updateCardAccountApplicationForOnboardingSuccess(
                                {},
                            ),
                        ];
                    }

                    const returnActions = [
                        reduxStepsSetStep(
                            CARD_ONBOARDING_MVP_2_STEPS,
                            getMVP2StepFromStatus(
                                cardAccountApplication.status,
                            ),
                        ),
                        reduxStepsSetStep(
                            Object.values(metalSteps),
                            getMetalStepFromStatus(
                                cardAccountApplication.status,
                            ),
                        ),
                        cardApplicationSetSteps(
                            getCardApplicationStepFromStatus(
                                cardAccountApplication.status,
                            ),
                        ),
                        updateCardAccountApplicationForOnboardingSuccess(
                            cardAccountApplication,
                        ),
                    ];
                    return returnActions;
                },
                errors: [updateCardAccountApplicationError().type],
                errorDispatch: (result) => {
                    const error = result.payload;
                    return [
                        updateCardAccountApplicationForOnboardingError(error),
                        reduxStepsSetStep(
                            CARD_ONBOARDING_MVP_2_STEPS,
                            APPLICATION_ERROR_MVP_2_STEP,
                        ),
                        reduxStepsSetStep(
                            Object.values(metalSteps),
                            metalSteps.APPLICATION_ERROR_STEP,
                        ),
                    ];
                },
            }),
        ),
    );

export const cardPinwheelTransitionToModalMVP2 = (action$, state$) =>
    action$.pipe(
        ofType(BACKEND_CREATE_DEDUCTION),
        switchMap(() =>
            dispatchObservable({
                action$,
                state$,
                waitFor: () => [STORE_REPLACE_DEDUCTION],
                waitForDispatch: () => [
                    reduxStepsSetStep(
                        CARD_ONBOARDING_MVP_2_STEPS,
                        PINWHEEL_STEP,
                    ),
                    reduxStepsSetStep(
                        Object.values(metalSteps),
                        metalSteps.PINWHEEL_STEP,
                    ),
                ],
            }),
        ),
    );

export const processCardApplication = (action$) =>
    action$.pipe(
        ofType(CARD_PROCESS_APPLICATION),
        mergeMap((action) => {
            if (statesIneligibleForCard.includes(action.payload.state)) {
                return EMPTY;
            }

            return [
                createCardAccountApplicationsDataModule.dataRequest(
                    action.payload.requestBody,
                ),
            ];
        }),
    );

export const processCardApplicationAddress = (action$) =>
    action$.pipe(
        ofType(CARD_PROCESS_APPLICATION_ADDRESS),
        mergeMap((action) => {
            if (statesIneligibleForCard.includes(action.payload.state)) {
                return EMPTY;
            }

            return [cardUpdateUserInfo(action.payload)];
        }),
    );

export const cardApplicationPayrollStart = (action$) =>
    action$.pipe(
        ofType(payrollPaymentSetupStart().type),
        mergeMap(() => [cardApplicationSetSteps(PAYROLL_PAYMENT_SETUP_STEP)]),
    );

export const cardApplicationPayrollComplete = (action$) =>
    action$.pipe(
        ofType(payrollPaymentSetupComplete().type),
        mergeMap(() => [cardApplicationSetSteps(SUCCESS_STEP)]),
    );
